/**
 * @dupe/generative-ui - Render Utilities
 *
 * Shared rendering utilities that work in both Node.js and browser environments.
 * These are non-React utilities for data binding and tree traversal.
 */

import type { UITree, UIElement, DataBindingPath } from './catalog'
import { normalizeBindingPath } from './catalog'

// =============================================================================
// DATA BINDING UTILITIES
// =============================================================================

/**
 * Resolve a JSON pointer path against a data object
 * Supports paths like "/report/title" or "/products/0/name"
 */
export function resolveJsonPointer(data: unknown, pointer: string): unknown {
  if (!pointer.startsWith('/')) {
    return undefined
  }

  const parts = pointer.slice(1).split('/')
  let current: unknown = data

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined
    }

    if (Array.isArray(current)) {
      const index = parseInt(part, 10)
      if (Number.isNaN(index)) {
        return undefined
      }
      current = current[index]
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }

  return current
}

/**
 * Apply a transform to a resolved value
 */
export function applyTransform(
  value: unknown,
  transform?: 'uppercase' | 'lowercase' | 'capitalize' | 'currency' | 'percent'
): unknown {
  if (value === undefined || value === null) return value

  switch (transform) {
    case 'uppercase':
      return String(value).toUpperCase()
    case 'lowercase':
      return String(value).toLowerCase()
    case 'capitalize':
      return String(value).charAt(0).toUpperCase() + String(value).slice(1)
    case 'currency': {
      const num = typeof value === 'number' ? value : parseFloat(String(value))
      if (Number.isNaN(num)) return value
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(num)
    }
    case 'percent': {
      const num = typeof value === 'number' ? value : parseFloat(String(value))
      if (Number.isNaN(num)) return value
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(num)
    }
    default:
      return value
  }
}

/**
 * Resolve a data binding path against a data context
 */
export function resolveBinding(
  binding: string | DataBindingPath,
  data: unknown
): unknown {
  const normalized = normalizeBindingPath(binding as string | { path: string })

  const resolved = resolveJsonPointer(data, normalized.path)

  // Apply transform if specified
  if (typeof binding === 'object' && 'transform' in binding) {
    const transformed = applyTransform(resolved, binding.transform)
    if (transformed !== undefined) return transformed
  }

  // Return fallback if value is undefined
  if (
    resolved === undefined &&
    typeof binding === 'object' &&
    'fallback' in binding
  ) {
    return binding.fallback
  }

  return resolved
}

/**
 * Check if a value is a data binding path
 */
export function isBindingPath(value: unknown): value is DataBindingPath {
  if (typeof value === 'string' && value.startsWith('/')) {
    return true
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'path' in value &&
    typeof (value as { path: unknown }).path === 'string'
  ) {
    return true
  }
  return false
}

/**
 * Resolve all binding paths in a props object
 */
export function resolvePropsBindings(
  props: Record<string, unknown>,
  data: unknown
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(props)) {
    if (isBindingPath(value)) {
      resolved[key] = resolveBinding(value, data)
    } else if (Array.isArray(value)) {
      resolved[key] = value.map((item) =>
        isBindingPath(item) ? resolveBinding(item, data) : item
      )
    } else if (typeof value === 'object' && value !== null) {
      // Recursively resolve nested objects (but not binding objects)
      if (!('path' in value)) {
        resolved[key] = resolvePropsBindings(
          value as Record<string, unknown>,
          data
        )
      } else {
        resolved[key] = value
      }
    } else {
      resolved[key] = value
    }
  }

  return resolved
}

// =============================================================================
// TREE TRAVERSAL UTILITIES
// =============================================================================

/**
 * Traverse a UI tree depth-first, calling the visitor for each element
 */
export function traverseTree(
  tree: UITree,
  visitor: (element: UIElement, depth: number) => void | false,
  startKey?: string
): void {
  const rootKey = startKey ?? tree.root

  function visit(key: string, depth: number): boolean {
    const element = tree.elements[key]
    if (!element) return true

    const result = visitor(element, depth)
    if (result === false) return false

    if (element.children) {
      for (const childKey of element.children) {
        const shouldContinue = visit(childKey, depth + 1)
        if (!shouldContinue) return false
      }
    }

    return true
  }

  visit(rootKey, 0)
}

/**
 * Find an element by key in the tree
 */
export function findElement(tree: UITree, key: string): UIElement | undefined {
  return tree.elements[key]
}

/**
 * Find all elements of a specific type
 */
export function findElementsByType(tree: UITree, type: string): UIElement[] {
  const results: UIElement[] = []
  traverseTree(tree, (element) => {
    if (element.type === type) {
      results.push(element)
    }
  })
  return results
}

/**
 * Get the parent of an element (if any)
 */
export function findParent(tree: UITree, key: string): UIElement | undefined {
  for (const element of Object.values(tree.elements)) {
    if (element.children?.includes(key)) {
      return element
    }
  }
  return undefined
}

/**
 * Get all ancestor elements from root to the specified element
 */
export function getAncestors(tree: UITree, key: string): UIElement[] {
  const ancestors: UIElement[] = []
  let current = key

  while (current) {
    const parent = findParent(tree, current)
    if (parent) {
      ancestors.unshift(parent)
      current = parent.key
    } else {
      break
    }
  }

  return ancestors
}

/**
 * Calculate the depth of an element in the tree
 */
export function getDepth(tree: UITree, key: string): number {
  return getAncestors(tree, key).length
}

// =============================================================================
// TREE MANIPULATION UTILITIES
// =============================================================================

/**
 * Create a deep clone of a UI tree
 */
export function cloneTree(tree: UITree): UITree {
  return JSON.parse(JSON.stringify(tree))
}

/**
 * Add an element to the tree
 */
export function addElement(
  tree: UITree,
  element: UIElement,
  parentKey?: string
): UITree {
  const newTree = cloneTree(tree)
  newTree.elements[element.key] = element

  if (parentKey) {
    const parent = newTree.elements[parentKey]
    if (parent) {
      parent.children = [...(parent.children || []), element.key]
    }
  }

  return newTree
}

/**
 * Remove an element from the tree (and all its descendants)
 */
export function removeElement(tree: UITree, key: string): UITree {
  const newTree = cloneTree(tree)

  // Find and remove all descendants
  const keysToRemove = new Set<string>([key])
  const collectDescendants = (k: string) => {
    const element = newTree.elements[k]
    if (element?.children) {
      for (const childKey of element.children) {
        keysToRemove.add(childKey)
        collectDescendants(childKey)
      }
    }
  }
  collectDescendants(key)

  // Remove from parent's children
  const parent = findParent(newTree, key)
  if (parent) {
    parent.children = parent.children?.filter((k) => k !== key)
  }

  // Remove all collected keys
  for (const k of keysToRemove) {
    delete newTree.elements[k]
  }

  return newTree
}

/**
 * Update an element's props
 */
export function updateElementProps(
  tree: UITree,
  key: string,
  propsUpdate: Record<string, unknown>
): UITree {
  const newTree = cloneTree(tree)
  const element = newTree.elements[key]

  if (element) {
    element.props = { ...element.props, ...propsUpdate }
  }

  return newTree
}

// =============================================================================
// DATA HASH UTILITIES
// =============================================================================

/**
 * Compute a hash of the data sources for drift detection
 * Uses a simple string hash for consistency
 */
export function computeDataHash(data: unknown): string {
  const str = stableStringify(data)
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }

  // Convert to hex string
  return Math.abs(hash).toString(16).padStart(8, '0')
}

/**
 * Check if data has drifted from the stored hash
 */
export function hasDataDrift(data: unknown, storedHash: string): boolean {
  const currentHash = computeDataHash(data)
  return currentHash !== storedHash
}

function stableStringify(value: unknown, seen = new WeakSet<object>()): string {
  if (value === null || value === undefined) return String(value)
  if (typeof value !== 'object') return JSON.stringify(value)

  if (seen.has(value as object)) {
    return '"[Circular]"'
  }
  seen.add(value as object)

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item, seen)).join(',')}]`
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([key, val]) => `${JSON.stringify(key)}:${stableStringify(val, seen)}`)

  return `{${entries.join(',')}}`
}

// =============================================================================
// VERSION UTILITIES
// =============================================================================

/**
 * Parse a version string like "ui-renderer@1.6.2"
 */
export function parseVersionString(
  version: string
): { name: string; version: string } | null {
  const match = version.match(/^(.+)@(\d+\.\d+\.\d+(?:-[\w.]+)?)$/)
  if (!match || !match[1] || !match[2]) return null
  return { name: match[1], version: match[2] }
}

/**
 * Create a version string
 */
export function createVersionString(name: string, version: string): string {
  return `${name}@${version}`
}

/**
 * Compare semantic versions
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareVersions(a: string, b: string): number {
  const aParts = a.split('.').map(Number)
  const bParts = b.split('.').map(Number)

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = aParts[i] || 0
    const bPart = bParts[i] || 0

    if (aPart < bPart) return -1
    if (aPart > bPart) return 1
  }

  return 0
}

/**
 * Check if a version is compatible (same major, >= minor.patch)
 */
export function isVersionCompatible(
  required: string,
  available: string
): boolean {
  const reqParts = required.split('.').map(Number)
  const avParts = available.split('.').map(Number)

  const reqMajor = reqParts[0] ?? 0
  const reqMinor = reqParts[1] ?? 0
  const reqPatch = reqParts[2] ?? 0
  const avMajor = avParts[0] ?? 0
  const avMinor = avParts[1] ?? 0
  const avPatch = avParts[2] ?? 0

  // Major must match
  if (reqMajor !== avMajor) return false

  // Available minor.patch must be >= required
  if (avMinor > reqMinor) return true
  if (avMinor < reqMinor) return false

  return avPatch >= reqPatch
}
